import React from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import { makeStyles } from "tss-react/mui";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Stack from "@mui/material/Stack";
import { listedRoomCardWidthPx } from "../../../utils/floors-graph-helper";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import FloorGraph from "./FloorGraph";
import Base64Image from "../../misc/Base64Image";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import Collapse from "@mui/material/Collapse";
import ListedRoomActions from "./ListedRoomActions";
import TempRoomActions from "./TempRoomActions";
import { useSharedSelector } from "../../../store/SharedProvider";
import { selectIsWebApp } from "../../../store/app-configurations/app-configurations-selectors";

const thumbnailMarginPx = 5;

const useStyles = makeStyles()((theme) => ({
  listedRoomCard: {
    width: `${listedRoomCardWidthPx}px`,
  },
  listedRoomCardClickable: {
    "&:hover": {
      background: "rgb(245, 245, 245)",
    },
    cursor: "pointer",
  },
  thumbnailWrapperDiv: {
    display: "flex",
    justifyContent: "space-between",
    margin: `${thumbnailMarginPx}px`,
    flexDirection: "column",
    padding: "1px",
    width: `${listedRoomCardWidthPx - 2 * thumbnailMarginPx}px`,
    height: `${listedRoomCardWidthPx - 2 * thumbnailMarginPx}px`,
  },
  thumbnailImageDiv: {
    backgroundColor: "rgb(205,205,205)",
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  thumbnailImage: {
    alignSelf: "center",
    maxWidth: "100%",
    maxHeight: "100%",
  },
  roomDescriptionContainer: {
    margin: `${2 * thumbnailMarginPx}px`,
  },
}));

const ListedRoom: React.FC<{
  roomMetaInfo?: RoomData;
  rooms?: RoomData[];
  name: string;
  mapRotation?: number;
  isFloorRoom?: boolean;
  isSelected?: boolean;
  onClickAway?: () => void;
  onClick: () => void;
  allowDelete?: boolean;
  isTempRoom?: boolean;
  showTransitionMappingButton?: boolean;
}> = ({
  name,
  roomMetaInfo,
  rooms,
  mapRotation,
  allowDelete = true,
  onClick,
  onClickAway,
  isTempRoom = false,
  isFloorRoom,
  isSelected,
  showTransitionMappingButton,
}) => {
  const { classes, cx } = useStyles();

  const { t } = useTranslation();

  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const clickHandler = () => {
    if (!isTempRoom) {
      onClick();
    }
  };

  const clickAwayHandler = () => {
    if (onClickAway != null && isSelected) {
      onClickAway();
    }
  };

  return (
    <ClickAwayListener onClickAway={clickAwayHandler}>
      <Grid item>
        <Card
          className={cx(classes.listedRoomCard, {
            [classes.listedRoomCardClickable]: !isTempRoom && !isSelected,
          })}
          onClick={!isSelected ? clickHandler : undefined}
        >
          <div className={classes.thumbnailWrapperDiv}>
            <div className={classes.thumbnailImageDiv}>
              {roomMetaInfo != null || rooms?.length === 1 ? (
                roomMetaInfo?.secondaryStation != null || (rooms?.length === 1 && rooms[0].secondaryStation != null) ? (
                  <SwapHorizRoundedIcon
                    sx={{
                      height: 128,
                      width: 128,
                      alignSelf: "center",
                      color: "white",
                      stroke: "black",
                      strokeWidth: 0.25,
                    }}
                  />
                ) : (
                  <Base64Image
                    imageString={(roomMetaInfo ?? rooms![0]).map}
                    alt={roomMetaInfo?.name ?? rooms![0].name}
                    style={{
                      transform: `rotate(${mapRotation}deg)`,
                      display: "flex",
                      justifyContent: "center",
                      margin: `${thumbnailMarginPx}px`,
                      padding: "1px",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      backgroundColor: "rgb(205,205,205)",
                    }}
                  />
                )
              ) : rooms != null ? (
                <FloorGraph rooms={rooms} />
              ) : null}
            </div>
            <Typography
              variant="subtitle2"
              whiteSpace="wrap"
              textAlign="center"
              sx={(theme) => ({ lineHeight: theme.spacing(4), pt: 0.5 })}
            >
              {name}
            </Typography>
          </div>

          <Collapse in={isFloorRoom !== true || isSelected}>
            <React.Fragment>
              {isSelected && (
                <Button onClick={onClick} fullWidth>
                  {t("button-open-room")}
                </Button>
              )}
              {isWebapp && (
                <React.Fragment>
                  <Divider />
                  <div className={classes.roomDescriptionContainer}>
                    {roomMetaInfo != null ? (
                      <React.Fragment>
                        {isTempRoom ? (
                          <TempRoomActions roomMetaInfo={roomMetaInfo} />
                        ) : (
                          <ListedRoomActions
                            roomMetaInfo={roomMetaInfo}
                            showTransitionMappingButton={showTransitionMappingButton}
                            allowDelete={allowDelete}
                          />
                        )}
                      </React.Fragment>
                    ) : (
                      <Stack spacing={1} my={1}>
                        <Typography variant="body2">{`${t("rooms-plural")}: ${
                          rooms?.filter((r) => r.secondaryStation == null).length ?? 1
                        }`}</Typography>
                        <Typography variant="body2">{`${t("transitions-plural")}: ${
                          rooms?.filter((r) => r.secondaryStation != null).length ?? 1
                        }`}</Typography>
                      </Stack>
                    )}
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          </Collapse>
        </Card>
      </Grid>
    </ClickAwayListener>
  );
};

export default ListedRoom;
